import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ArrowLeft,
  ChevronDown,
  FileDown,
  Printer,
  Type,
} from 'lucide-react';
import { useReport, type ReportBlock } from '../context/ReportContext';

// ─── Print-only CSS injected once ────────────────────────────────────────────

const PRINT_STYLE_ID = 'report-studio-print-styles';

function ensurePrintStyles() {
  if (document.getElementById(PRINT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PRINT_STYLE_ID;
  style.textContent = `
@media print {
  /* Hide everything that is not the A4 sheet */
  body > *:not(#report-studio-root) { display: none !important; }
  #report-studio-root { position: static !important; background: white !important; }
  #report-studio-ribbon { display: none !important; }
  #report-studio-sheet {
    box-shadow: none !important;
    margin: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    min-height: auto !important;
    page-break-after: avoid;
  }
  [contenteditable="true"]:focus { outline: none !important; }
  [data-print-hide] { display: none !important; }
}
@page {
  size: A4 portrait;
  margin: 15mm 20mm;
}
  `;
  document.head.appendChild(style);
}

// ─── Font size options ────────────────────────────────────────────────────────

const FONT_SIZES = [9, 10, 11, 12, 13, 14] as const;

// ─── Editable block ───────────────────────────────────────────────────────────

interface EditableBlockProps {
  block: ReportBlock;
  fontSize: number;
  onContentChange: (id: string, content: string) => void;
}

const EditableBlock: React.FC<EditableBlockProps> = ({ block, fontSize, onContentChange }) => {
  const ref = useRef<HTMLDivElement & HTMLHeadingElement & HTMLParagraphElement>(null);

  // Sync content prop → DOM only if focus is elsewhere (avoid fighting with user typing)
  useEffect(() => {
    if (!ref.current) return;
    if (document.activeElement !== ref.current && block.content !== undefined) {
      ref.current.textContent = block.content;
    }
  }, [block.content]);

  const handleBlur = useCallback(() => {
    if (ref.current) {
      onContentChange(block.id, ref.current.textContent ?? '');
    }
  }, [block.id, onContentChange]);

  const sharedProps = {
    ref,
    contentEditable: true as const,
    suppressContentEditableWarning: true,
    onBlur: handleBlur,
    'data-placeholder': block.type === 'heading' ? 'Título do relatório…'
      : block.type === 'subheading' ? 'Subtítulo ou seção…'
      : 'Texto do parágrafo…',
    className:
      'focus:outline-none focus:ring-1 focus:ring-blue-300/50 rounded-sm px-0.5 -mx-0.5 empty:before:content-[attr(data-placeholder)] empty:before:text-gray-300',
  };

  if (block.type === 'heading') {
    return (
      <h1
        {...sharedProps}
        style={{ fontSize: `${fontSize + 7}pt`, lineHeight: 1.25, fontWeight: 700, marginBottom: '6pt', color: '#111827' }}
      />
    );
  }
  if (block.type === 'subheading') {
    return (
      <h2
        {...sharedProps}
        style={{ fontSize: `${fontSize + 2}pt`, lineHeight: 1.35, fontWeight: 600, marginBottom: '4pt', marginTop: '10pt', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '3pt' }}
      />
    );
  }
  if (block.type === 'richtext') {
    return (
      <div
        {...sharedProps}
        style={{ fontSize: `${fontSize}pt`, lineHeight: 1.65, color: '#374151', whiteSpace: 'pre-wrap', marginBottom: '6pt' }}
      />
    );
  }
  // paragraph (default)
  return (
    <p
      {...sharedProps}
      style={{ fontSize: `${fontSize}pt`, lineHeight: 1.65, color: '#374151', marginBottom: '6pt' }}
    />
  );
};

// ─── Custom block wrapper ─────────────────────────────────────────────────────

const CustomBlock: React.FC<{ block: ReportBlock; fontSize: number }> = ({ block, fontSize }) => (
  <div style={{ marginBottom: '10pt' }}>
    {block.label && (
      <p style={{ fontSize: `${fontSize - 1}pt`, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4pt' }}>
        {block.label}
      </p>
    )}
    <div style={{ fontSize: `${fontSize}pt` }}>
      {block.element}
    </div>
  </div>
);

// ─── Main overlay ─────────────────────────────────────────────────────────────

export const ReportStudioOverlay: React.FC = () => {
  const {
    isReportMode,
    closeReport,
    reportTitle,
    reportSubtitle,
    reportOrganizationName,
    reportBlocks,
    fontSize,
    setFontSize,
    updateBlockContent,
  } = useReport();

  const sheetRef = useRef<HTMLDivElement>(null);
  const [showFontMenu, setShowFontMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const fontMenuRef = useRef<HTMLDivElement>(null);

  // Inject print CSS once
  useEffect(() => {
    if (isReportMode) ensurePrintStyles();
  }, [isReportMode]);

  // Close font dropdown on outside click
  useEffect(() => {
    if (!showFontMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (fontMenuRef.current && !fontMenuRef.current.contains(e.target as Node)) {
        setShowFontMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showFontMenu]);

  // Escape key to close
  useEffect(() => {
    if (!isReportMode) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeReport();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isReportMode, closeReport]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleExportPDF = useCallback(async () => {
    if (!sheetRef.current) return;
    setIsExporting(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      const canvas = await html2canvas(sheetRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      // A4 in mm: 210 x 297
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      let yPos = 0;
      const pageHeight = pdf.internal.pageSize.getHeight();
      while (yPos < pdfHeight) {
        if (yPos > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, -yPos, pdfWidth, pdfHeight);
        yPos += pageHeight;
      }

      const safeTitle = reportTitle.replace(/[^a-zA-Z0-9\-_]/g, '_').slice(0, 40) || 'relatorio';
      pdf.save(`${safeTitle}.pdf`);
    } finally {
      setIsExporting(false);
    }
  }, [reportTitle]);

  if (!isReportMode) return null;

  const today = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div
      id="report-studio-root"
      className="fixed inset-0 z-[9999] flex flex-col overflow-hidden"
      style={{ background: 'var(--report-studio-bg, #d1d5db)' }}
    >
      {/* ── Report Ribbon ─────────────────────────────────────────────────── */}
      <div
        id="report-studio-ribbon"
        className="shrink-0 border-b border-slate-300/60 backdrop-blur-sm"
        style={{ height: '44px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', backgroundColor: 'rgba(255,255,255,0.97)' }}
      >
        <div className="flex h-full items-center gap-1 px-3">
          {/* Sair */}
          <button
            type="button"
            onClick={closeReport}
            className="inline-flex h-7 items-center gap-1.5 rounded border border-transparent px-2 text-[11px] font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
            title="Sair do modo relatório (Esc)"
          >
            <ArrowLeft size={13} strokeWidth={1.8} />
            Sair
          </button>

          <div className="mx-2 h-5 w-px bg-slate-200" />

          {/* Imprimir */}
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex h-7 items-center gap-1.5 rounded border border-transparent px-2 text-[11px] font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
            title="Imprimir (Ctrl+P)"
          >
            <Printer size={13} strokeWidth={1.8} />
            Imprimir
          </button>

          {/* Baixar PDF */}
          <button
            type="button"
            onClick={handleExportPDF}
            disabled={isExporting}
            className="inline-flex h-7 items-center gap-1.5 rounded border border-transparent px-2 text-[11px] font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50"
            title="Exportar como PDF"
          >
            <FileDown size={13} strokeWidth={1.8} />
            {isExporting ? 'Gerando…' : 'Baixar PDF'}
          </button>

          <div className="mx-2 h-5 w-px bg-slate-200" />

          {/* Tamanho da fonte */}
          <div className="relative" ref={fontMenuRef}>
            <button
              type="button"
              onClick={() => setShowFontMenu((v) => !v)}
              className="inline-flex h-7 items-center gap-1.5 rounded border border-transparent px-2 text-[11px] font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
              title="Tamanho da fonte"
            >
              <Type size={13} strokeWidth={1.8} />
              Fonte {fontSize}pt
              <ChevronDown size={10} className="opacity-60" />
            </button>
            {showFontMenu && (
              <div className="absolute left-0 top-full z-[100] mt-1 overflow-hidden rounded border border-slate-200 bg-white py-1 shadow-lg">
                {FONT_SIZES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => { setFontSize(s); setShowFontMenu(false); }}
                    className={`flex w-full items-center gap-2 px-3 py-1 text-[12px] transition-colors hover:bg-slate-50 ${
                      fontSize === s ? 'bg-slate-100 font-semibold text-slate-900' : 'text-slate-700'
                    }`}
                  >
                    {s}pt {fontSize === s && '✓'}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Page info */}
          <div className="ml-auto text-[10px] text-slate-400 tabular-nums">
            A4 · 210 × 297 mm
          </div>
        </div>
      </div>

      {/* ── Scrollable canvas background ───────────────────────────────────── */}
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-8">
        {/* ── A4 Sheet ───────────────────────────────────────────────────── */}
        <div
          id="report-studio-sheet"
          ref={sheetRef}
          className="mx-auto shadow-2xl"
          style={{
            width: '794px',
            minHeight: '1123px',
            padding: '20mm',
            boxSizing: 'border-box',
            fontFamily: '"Inter", "Roboto", "Segoe UI", sans-serif',
            fontSize: `${fontSize}pt`,
            backgroundColor: '#ffffff',
            color: '#1f2937',
            position: 'relative',
          }}
        >
          {/* ── Sheet Header ─────────────────────────────────────────────── */}
          <div style={{ marginBottom: '16pt', borderBottom: '2px solid #1e40af', paddingBottom: '8pt', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div>
              <div
                style={{ fontSize: `${fontSize - 1}pt`, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '3pt' }}
              >
                {reportOrganizationName || 'Workflow Manager'}
              </div>
              <div
                style={{ fontSize: `${fontSize + 8}pt`, fontWeight: 800, color: '#111827', lineHeight: 1.15 }}
              >
                {reportTitle || 'Relatório'}
              </div>
              {reportSubtitle && (
                <div style={{ fontSize: `${fontSize + 1}pt`, color: '#4b5563', marginTop: '3pt' }}>
                  {reportSubtitle}
                </div>
              )}
            </div>
            <div style={{ fontSize: `${fontSize - 1}pt`, color: '#9ca3af', textAlign: 'right', flexShrink: 0, paddingLeft: '12pt' }}>
              <div>{today}</div>
              <div style={{ marginTop: '2pt', color: '#d1d5db' }}>Gerado por Workflow Manager</div>
            </div>
          </div>

          {/* ── Blocks ───────────────────────────────────────────────────── */}
          {reportBlocks.length === 0 ? (
            <div
              style={{ color: '#d1d5db', fontSize: `${fontSize}pt`, fontStyle: 'italic', marginTop: '20pt' }}
              data-print-hide
            >
              Nenhum conteúdo adicionado. Use <code>openReport(&#123; blocks: [...] &#125;)</code> para injetar dados.
            </div>
          ) : (
            reportBlocks.map((block) => {
              if (block.type === 'divider') {
                return <hr key={block.id} style={{ border: 'none', borderTop: '1px solid #e5e7eb', marginTop: '10pt', marginBottom: '10pt' }} />;
              }
              if (block.type === 'custom') {
                return <CustomBlock key={block.id} block={block} fontSize={fontSize} />;
              }
              return (
                <EditableBlock
                  key={block.id}
                  block={block}
                  fontSize={fontSize}
                  onContentChange={updateBlockContent}
                />
              );
            })
          )}

          {/* ── Sheet Footer ─────────────────────────────────────────────── */}
          <div
            style={{
              position: 'absolute',
              bottom: '12mm',
              left: '20mm',
              right: '20mm',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: `${fontSize - 2}pt`,
              color: '#d1d5db',
              borderTop: '1px solid #f3f4f6',
              paddingTop: '4pt',
            }}
          >
            <span>Workflow Manager · Report Studio</span>
            <span>{today}</span>
          </div>
        </div>

        {/* Spacer so footer isn't hidden behind browser chrome */}
        <div style={{ height: '40px' }} />
      </div>
    </div>
  );
};

export default ReportStudioOverlay;
